import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AUTH_TOKEN_PARAM, SSO_ENDPOINTS } from '@airclou/auth';
const AuthContext = createContext(undefined);
export function AuthProvider({ children, config }) {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState('loading');
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get(AUTH_TOKEN_PARAM);
        if (token) {
            params.delete(AUTH_TOKEN_PARAM);
            const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
            window.history.replaceState({}, '', newUrl);
        }
        fetchCurrentUser();
    }, []);
    const fetchCurrentUser = async () => {
        try {
            const response = await fetch(`${config.authServiceUrl}${SSO_ENDPOINTS.SESSION}`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
                setStatus('authenticated');
            }
            else {
                setUser(null);
                setStatus('unauthenticated');
            }
        }
        catch (error) {
            console.error('Failed to fetch user:', error);
            setUser(null);
            setStatus('unauthenticated');
        }
    };
    const login = useCallback((options) => {
        const params = new URLSearchParams({
            app: config.appId,
        });
        if (options?.tenantId) {
            params.set('tenant', options.tenantId);
        }
        const redirect = options?.redirect || window.location.pathname + window.location.search;
        params.set('redirect', redirect);
        window.location.href = `${config.authServiceUrl}${SSO_ENDPOINTS.LOGIN}?${params.toString()}`;
    }, [config]);
    const logout = useCallback(async () => {
        try {
            await fetch(`${config.authServiceUrl}${SSO_ENDPOINTS.LOGOUT}`, {
                method: 'POST',
                credentials: 'include',
            });
        }
        catch (error) {
            console.error('Logout failed:', error);
        }
        setUser(null);
        setStatus('unauthenticated');
        window.location.href = '/';
    }, [config]);
    const refreshSession = useCallback(async () => {
        await fetchCurrentUser();
    }, []);
    const value = {
        user,
        status,
        login,
        logout,
        refreshSession,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
export function RequireAuth({ children, fallback }) {
    const { user, status, login } = useAuth();
    useEffect(() => {
        if (status === 'unauthenticated') {
            login();
        }
    }, [status, login]);
    if (status === 'loading') {
        return fallback || _jsx("div", { children: "Loading..." });
    }
    if (!user) {
        return fallback || null;
    }
    return _jsx(_Fragment, { children: children });
}
